<template>
	<div class="desktop-analysis-page" :class="colourMode">
		<SettingsOverlay
			v-if="showSettings"
			:customer="customer"
			:colourMode="colourMode"
			:safeTop="safeTop"
			@close="showSettings = false"
			@update-pref="updatePref"
			@select-colour-mode="selectColourMode"
		/>
		<TransactionsDrawer
			v-model="showDrawer"
			:title="drawerTitle"
			:transactions="drawerTransactions"
			:plan="drawerPlan"
			:formatter="formatter"
			:colourMode="colourMode"
			@close="showDrawer = false"
			@transaction-click="openTaggingModal"
		/>
		<TransactionTaggingModal
			:visible="showTaggingModal"
			:transactionData="selectedTransaction"
			:colourMode="colourMode"
			:continuousMode="continuousMode"
			:continuousModeIndex="continuousModeIndex"
			:continuousModeTotal="continuousMode ? uncategorizedTransactions.length : 0"
			@close="closeTaggingModal"
			@tagged="onTransactionTagged"
			@next="onNextUncategorized"
		/>
		<!-- Loading State -->
		<div v-if="!ready" class="loading-state">
			<v-skeleton-loader type="card" height="120px" class="mb-4" />
			<v-skeleton-loader type="card" height="300px" class="mb-4" />
		</div>

		<template v-else>
			<!-- Summary Header -->
			<div class="summary-header">
				<div class="summary-stats">
					<div class="stat-item">
						<span class="stat-label">Income</span>
						<span class="stat-value income">{{ formatter.format(totalIncome) }}</span>
					</div>
					<div class="stat-item">
						<span class="stat-label">Spent</span>
						<span class="stat-value spent">{{ formatter.format(totalSpend) }}</span>
					</div>
					<div class="stat-item">
						<span class="stat-label">Net</span>
						<span class="stat-value" :class="netAmount >= 0 ? 'positive' : 'negative'">
							{{ formatter.format(netAmount) }}
						</span>
					</div>
					<div class="stat-item">
						<span class="stat-label">Days to Payday</span>
						<span class="stat-value">{{ daysToPayday }}</span>
					</div>
				</div>
				<div class="month-selector-wrapper">
					<button
						v-show="canScrollMonthsLeft"
						class="month-scroll-arrow month-scroll-left"
						@click="scrollMonths('left')"
					>
						<v-icon size="20">mdi-chevron-left</v-icon>
					</button>
					<div class="month-selector" ref="monthSelector" @scroll="updateMonthScrollArrows">
						<button
							v-for="slot in displaySlots"
							:key="slot.key"
							:class="['month-btn', { active: selectedSlotKey === slot.key }]"
							@click="selectedSlotKey = slot.key"
						>
							{{ slot.label }}
						</button>
					</div>
					<button
						v-show="canScrollMonthsRight"
						class="month-scroll-arrow month-scroll-right"
						@click="scrollMonths('right')"
					>
						<v-icon size="20">mdi-chevron-right</v-icon>
					</button>
				</div>
			</div>

			<!-- Uncategorized Banner -->
			<div v-if="uncategorizedTransactions.length > 0" class="uncategorized-banner">
				<div class="banner-content">
					<v-icon size="20">mdi-tag-outline</v-icon>
					<span class="banner-text">
						You have <strong>{{ uncategorizedTransactions.length }}</strong> uncategorised transaction{{ uncategorizedTransactions.length === 1 ? '' : 's' }}
					</span>
				</div>
				<button class="start-categorizing-btn" @click="startCategorizing">
					Start Categorising
				</button>
			</div>

			<!-- Charts Row 1: Donut + Trend -->
			<div class="charts-row">
				<div class="chart-card donut-card">
					<h3 class="chart-title">Spending Category Breakdown</h3>
					<SpendingDonutChart
						:categoryTotals="categoryTotals"
						:formatter="formatter"
						:colourMode="colourMode"
						@category-click="handleCategoryClick"
					/>
				</div>
				<div class="chart-card trend-card">
					<h3 class="chart-title">12-Month Trends</h3>
					<SpendingTrendChart
						:trendData="trendData"
						:formatter="formatter"
						:colourMode="colourMode"
					/>
				</div>
			</div>

			<!-- Charts Row 2: Income vs Spend -->
			<div class="charts-row">
				<div class="chart-card full-width">
					<h3 class="chart-title">Income vs Spend</h3>
					<IncomeVsSpendChart
						:monthlyData="monthlyData"
						:formatter="formatter"
						:colourMode="colourMode"
					/>
				</div>
			</div>

			<!-- Charts Row 3: Top Merchants -->
			<div class="charts-row">
				<div class="chart-card full-width">
					<h3 class="chart-title">Top Merchants</h3>
					<TopMerchantsChart
						:merchants="topMerchants"
						:formatter="formatter"
						:colourMode="colourMode"
						@merchant-click="handleMerchantClick"
					/>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import Services from '../../mixins/Services';
import SpendingAnalysis, { GRANDPARENT_CLASSIFICATIONS } from '../../mixins/SpendingAnalysis';
import SpendingPlans from '../../mixins/SpendingPlans';
import SpendingPlansSidebar from '../../mixins/SpendingPlansSidebar';
import SpendingDonutChart from '../components/SpendingDonutChart.vue';
import SpendingTrendChart from '../components/SpendingTrendChart.vue';
import TopMerchantsChart from '../components/TopMerchantsChart.vue';
import IncomeVsSpendChart from '../components/IncomeVsSpendChart.vue';
import SettingsOverlay from '../components/SettingsOverlay.vue';
import TransactionsDrawer from '../components/TransactionsDrawer.vue';
import TransactionTaggingModal from '../components/TransactionTaggingModal.vue';

export default {
	name: "SpendingAnalysisPage",
	mixins: [Services, SpendingAnalysis, SpendingPlans, SpendingPlansSidebar],
	components: {
		SpendingDonutChart,
		SpendingTrendChart,
		TopMerchantsChart,
		IncomeVsSpendChart,
		SettingsOverlay,
		TransactionsDrawer,
		TransactionTaggingModal
	},
	emits: ['colour-mode-changed'],
	data() {
		return {
			colourMode: 'light',
			ready: false,
			slots: [],
			selectedSlotKey: null,
			userName: 'there',
			showSettings: false,
			showDrawer: false,
			drawerTitle: 'Transactions',
			drawerTransactions: [],
			drawerPlan: null,
			showTaggingModal: false,
			selectedTransaction: null,
			continuousMode: false,
			continuousModeIndex: 0,
			canScrollMonthsLeft: false,
			canScrollMonthsRight: false
		};
	},
	watch: {
		selectedSlotKey() {
			// Rebuild sidebar items when slot changes
			if (this.ready) {
				this.buildSpendingPlansSidebarItems(this.selectedSlotKey);
				this.registerSpendingPlansSidebar();
			}
		}
	},
	computed: {
		safeTop() {
			return parseInt(this.$cookies.get('st') ?? 0);
		},
		displaySlots() {
			if (!this.slots || this.slots.length === 0) return [];
			// Show all slots in reverse order (most recent first)
			return [...this.slots].reverse();
		},
		selectedSlotData() {
			if (!this.spendingAnalysisStructured?.grandparentAnalysis) return null;
			return this.spendingAnalysisStructured.grandparentAnalysis;
		},
		categoryTotals() {
			if (!this.selectedSlotData) return [];
			const totals = [];
			const categories = [
				{ id: GRANDPARENT_CLASSIFICATIONS.INCOME, name: 'Income', colour: '#B4D8D7' },
				{ id: GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING, name: 'Future Plans', colour: '#D5C9C8' },
				{ id: GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND, name: 'Everyday', colour: '#9EA2D0' },
				{ id: GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS, name: 'Committed', colour: '#413655' }
			];

			categories.forEach(cat => {
				const data = this.selectedSlotData.filter(d => d.grandparent === cat.id);
				const slotData = data.find(d => d.slotName === this.selectedSlotKey);
				totals.push({
					id: cat.id,
					name: cat.name,
					colour: cat.colour,
					value: slotData ? Math.abs(slotData.totalNet) : 0
				});
			});
			return totals;
		},
		totalIncome() {
			const income = this.categoryTotals.find(c => c.id === GRANDPARENT_CLASSIFICATIONS.INCOME);
			return income?.value || 0;
		},
		totalSpend() {
			return this.categoryTotals
				.filter(c => c.id !== GRANDPARENT_CLASSIFICATIONS.INCOME)
				.reduce((sum, c) => sum + c.value, 0);
		},
		netAmount() {
			return this.totalIncome - this.totalSpend;
		},
		daysToPayday() {
			return this.spendingAnalysisStructured?.daysTillCycleResets || 0;
		},
		trendData() {
			if (!this.spendingAnalysisStructured?.grandparentAnalysis || !this.slots) return [];
			const data = [];
			this.slots.forEach(slot => {
				const slotTotals = { label: slot.label };
				[GRANDPARENT_CLASSIFICATIONS.INCOME, GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING,
				GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND, GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS].forEach(gp => {
					const gpData = this.spendingAnalysisStructured.grandparentAnalysis
						.filter(d => d.grandparent === gp)
						.find(d => d.slotName === slot.key);
					slotTotals[gp] = gpData ? Math.abs(gpData.totalNet) : 0;
				});
				data.push(slotTotals);
			});
			return data;
		},
		monthlyData() {
			if (!this.trendData) return [];
			return this.trendData.slice(-12);
		},
		topMerchants() {
			if (!this.periodTransactions || this.periodTransactions.length === 0) return [];
			const merchantTotals = {};
			this.periodTransactions.forEach(t => {
				if (t.amount < 0) {
					const name = t.merchantName || t.merchant?.name || 'Unknown';
					merchantTotals[name] = (merchantTotals[name] || 0) + Math.abs(t.amount);
				}
			});
			return Object.entries(merchantTotals)
				.map(([name, value]) => ({ name, value }))
				.sort((a, b) => b.value - a.value)
				.slice(0, 5);
		},
		activePlans() {
			if (!this.spendingPlans?.plans) return [];
			return this.spendingPlans.plans.filter(p => p.plan?.amount > 0);
		},
		selectedSlot() {
			return this.slots.find(s => s.key === this.selectedSlotKey);
		},
		allTransactions() {
			const txns = this.bankInfo?.recentTransactions;
			if (!txns || !Array.isArray(txns)) return [];
			const flat = [];
			txns.forEach(group => {
				if (!Array.isArray(group)) return;
				group.forEach(t => flat.push(t));
			});
			return flat;
		},
		periodTransactions() {
			const slot = this.selectedSlot;
			if (!slot || !this.allTransactions.length) return this.allTransactions;

			const fromDate = slot.from ? new Date(slot.from) : null;
			const toDate = slot.to ? new Date(slot.to) : null;

			const filtered = this.allTransactions.filter(t => {
				const txDate = new Date(t.timestamp || t.transactionDate);
				if (fromDate && txDate < fromDate) return false;
				if (toDate && txDate > toDate) return false;
				return true;
			});

			// Fall back to all transactions if period has none
			return filtered.length > 0 ? filtered : this.allTransactions;
		},
		uncategorizedTransactions() {
			return this.allTransactions.filter(t => {
				const classification = t.customerClassification || t.effectiveClassification;
				return !classification || classification.mgClassification === 'Uncategorized';
			});
		}
	},
	methods: {
		scrollMonths(direction) {
			const container = this.$refs.monthSelector;
			if (!container) return;
			const scrollAmount = 200;
			container.scrollBy({
				left: direction === 'left' ? -scrollAmount : scrollAmount,
				behavior: 'smooth'
			});
		},
		updateMonthScrollArrows() {
			const container = this.$refs.monthSelector;
			if (!container) return;
			this.canScrollMonthsLeft = container.scrollLeft > 0;
			this.canScrollMonthsRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 1;
		},
		scrollToSelectedMonth() {
			this.$nextTick(() => {
				const container = this.$refs.monthSelector;
				if (!container) return;
				const selectedBtn = container.querySelector('.month-btn.active');
				if (selectedBtn) {
					selectedBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
				}
				this.updateMonthScrollArrows();
			});
		},
		loadData() {
			this._getCustomer(() => {
				if (this.customer?.person?.firstName) {
					this.userName = this.customer.person.firstName;
				}
				this._getBankingInfo(() => {
					this._getSpendingAnalysisStructured(() => {
						this._getSpendingPlansFull(() => {
							this._extractActivePlans();
							this.slots = this.spendingAnalysisStructured?.slotNames || [];
							this.selectedSlotKey = this.slots.length > 1
								? this.slots[this.slots.length - 2].key
								: (this.slots[0]?.key || '');
							this.ready = true;
							this.scrollToSelectedMonth();
							this.buildSpendingPlansSidebarItems(this.selectedSlotKey);
							this.registerSpendingPlansSidebar();
						});
					});
				});
			});
		},
		handleCategoryClick(categoryId) {
			// Handle donut chart category clicks
			const categoryNames = {
				0: 'Income',
				1: 'Future Plans',
				2: 'Everyday',
				3: 'Committed'
			};
			this.drawerTitle = categoryNames[categoryId] || 'Transactions';
			this.drawerPlan = null;
			this.drawerTransactions = this.periodTransactions.filter(t => {
				const classification = t.customerClassification || t.effectiveClassification;
				return classification?.grandParentClassification === categoryId;
			});
			this.showDrawer = true;
		},
		onSpendingPlanSelected(planData) {
			// Called by the mixin when a plan is clicked in the sidebar
			this.drawerTitle = planData.name || 'Plan';
			this.drawerPlan = {
				name: planData.name,
				iconPath: planData.iconPath,
				budget: planData.budget,
				spent: planData.spent,
				remaining: planData.remaining,
				percent: planData.percent
			};
			this.drawerTransactions = this.periodTransactions.filter(t => {
				const classification = t.customerClassification || t.effectiveClassification;
				return String(classification?.id) === String(planData.classificationId);
			});
			this.showDrawer = true;
		},
		handleMerchantClick(merchantName) {
			this.drawerTitle = merchantName;
			this.drawerPlan = null;
			this.drawerTransactions = this.periodTransactions.filter(t => {
				const name = t.merchantName || t.merchant?.name || 'Unknown';
				return name === merchantName;
			});
			this.showDrawer = true;
		},
		selectColourMode(mode) {
			this.colourMode = mode;
			localStorage.setItem('mg_colour_mode', mode);
			this.$emit('colour-mode-changed', mode);
		},
		updatePref(key, value) {
			if (this.customer?.preferences) {
				this.customer.preferences[key] = value;
				this._savePreferences();
			}
		},
		openTaggingModal(txn) {
			this.selectedTransaction = txn;
			this.continuousMode = false;
			this.showTaggingModal = true;
		},
		startCategorizing() {
			if (this.uncategorizedTransactions.length === 0) return;
			this.continuousMode = true;
			this.continuousModeIndex = 0;
			this.selectedTransaction = this.uncategorizedTransactions[0];
			this.showTaggingModal = true;
		},
		closeTaggingModal() {
			this.showTaggingModal = false;
			this.selectedTransaction = null;
			this.continuousMode = false;
			this.continuousModeIndex = 0;
		},
		onNextUncategorized() {
			// Move to next uncategorized transaction
			const remaining = this.uncategorizedTransactions;
			if (remaining.length === 0) {
				this.closeTaggingModal();
				return;
			}
			// Stay at index 0 since we just removed one from the list
			this.continuousModeIndex = 0;
			this.selectedTransaction = remaining[0];
		},
		onTransactionTagged(updatedTxn) {
			// Update the transaction in drawerTransactions
			const index = this.drawerTransactions.findIndex(t => t.id === updatedTxn.id);
			if (index !== -1) {
				this.drawerTransactions[index] = { ...this.drawerTransactions[index], ...updatedTxn };
			}
			// Reload data to refresh charts and totals
			this.loadData();
		}
	},
	mounted() {
		const savedMode = localStorage.getItem('mg_colour_mode');
		this.colourMode = savedMode || 'light';
		this.loadData();
	}
};
</script>

<style scoped>
.desktop-analysis-page {
	height: 100%;
	overflow-y: auto;
	padding: 24px;
	max-width: 1400px;
	margin: 0 auto;
}

/* Summary Header */
.summary-header {
	background: var(--light-bg-primary);
	border-radius: 12px;
	padding: 20px 24px;
	margin-bottom: 24px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
}

.summary-stats {
	display: flex;
	gap: 32px;
	flex-wrap: wrap;
}

.stat-item {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.stat-label {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.stat-value {
	font-size: 24px;
	color: var(--light-text-primary);
}

.stat-value.income { color: var(--category-income); }
.stat-value.spent { color: var(--brand-purple); }
.stat-value.positive { color: var(--positive-amount, #2e7d32); }
.stat-value.negative { color: var(--delete-color, #c62828); }

.month-selector-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
	max-width: 400px;
	position: relative;
}

.month-selector {
	display: flex;
	gap: 8px;
	overflow-x: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;
	flex: 1;
}

.month-selector::-webkit-scrollbar {
	display: none;
}

.month-scroll-arrow {
	width: 28px;
	height: 28px;
	border: 1px solid var(--light-border);
	border-radius: 50%;
	background: var(--light-bg-primary);
	color: var(--light-text-secondary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	transition: all 0.15s;
}

.month-scroll-arrow:hover {
	background: var(--light-bg-hover);
	color: var(--light-text-primary);
}

.month-btn {
	padding: 8px 16px;
	border: 1px solid var(--light-border);
	border-radius: 20px;
	background: transparent;
	color: var(--light-text-primary);
	cursor: pointer;
	font-size: 13px;
	transition: all 0.2s;
	flex-shrink: 0;
	white-space: nowrap;
}

.month-btn:hover {
	background: var(--light-bg-hover);
}

.month-btn.active {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
	color: white;
}

/* Charts */
.charts-row {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 24px;
	margin-bottom: 24px;
}

.chart-card {
	background: var(--light-bg-primary);
	border-radius: 12px;
	padding: 20px;
}

.chart-card.full-width {
	grid-column: 1 / -1;
}

.chart-title {
	font-size: 16px;
	margin: 0 0 16px 0;
	color: var(--light-text-primary);
}

/* Loading */
.loading-state {
	padding: 20px 0;
}

/* Uncategorized Banner */
.uncategorized-banner {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 20px;
	background: var(--suggestion-bg, #f0edf5);
	border: 1px solid var(--suggestion-border, #d4cce0);
	border-radius: 8px;
	margin-bottom: 24px;
}

.banner-content {
	display: flex;
	align-items: center;
	gap: 10px;
	color: var(--brand-purple);
}

.banner-text {
	font-size: 14px;
	color: var(--light-text-primary);
}

.start-categorizing-btn {
	background: var(--brand-purple);
	border: none;
	padding: 8px 16px;
	border-radius: 6px;
	font-size: 13px;
	cursor: pointer;
	color: white;
	transition: background 0.15s;
}

.start-categorizing-btn:hover {
	background: var(--brand-purple-light);
}

@media (max-width: 900px) {
	.charts-row {
		grid-template-columns: 1fr;
	}
}
</style>

<!-- Dark Mode -->
<style>
.desktop-analysis-page.dark {
	background: var(--dark-bg-primary);
}

.desktop-analysis-page.dark .summary-header,
.desktop-analysis-page.dark .chart-card {
	background: var(--dark-bg-secondary);
}

.desktop-analysis-page.dark .stat-label {
	color: var(--dark-text-secondary);
}

.desktop-analysis-page.dark .stat-value,
.desktop-analysis-page.dark .chart-title {
	color: var(--dark-text-primary);
}

.desktop-analysis-page.dark .month-btn {
	border-color: var(--dark-border);
	color: var(--dark-text-primary);
}

.desktop-analysis-page.dark .month-btn:hover {
	background: var(--dark-bg-hover);
}

.desktop-analysis-page.dark .month-scroll-arrow {
	border-color: var(--dark-border);
	background: var(--dark-bg-secondary);
	color: var(--dark-text-secondary);
}

.desktop-analysis-page.dark .month-scroll-arrow:hover {
	background: var(--dark-bg-hover);
	color: var(--dark-text-primary);
}

.desktop-analysis-page.dark .month-btn.active {
	background: var(--brand-purple-light);
}

/* Uncategorized Banner Dark Mode */
.desktop-analysis-page.dark .uncategorized-banner {
	background: var(--suggestion-bg-dark, rgba(65, 54, 85, 0.3));
	border-color: var(--suggestion-border-dark, #5a4a70);
}

.desktop-analysis-page.dark .banner-text {
	color: var(--dark-text-primary);
}
</style>
